import {
  AI_BUILDER_MENTION_ID,
  AI_BUILDER_MENTION_TYPE,
  mentionMarkup,
  parseMentionTokens,
  parseMentions,
  taskContextKey,
} from '@shared/task-contexts';
import type { TaskContext, TaskContextType } from '@shared/types/tasks';

/**
 * The textarea shows `@Acme Knives`; storage uses `@[Acme Knives](supplier:uuid)`.
 * These convert between the two, keeping the picked targets on the side.
 */
export interface PickedMention {
  label: string;
  type: 'user' | 'ai' | TaskContextType;
  id: string;
}

export function markupToDisplay(markup: string): { text: string; mentions: PickedMention[] } {
  const tokens = parseMentionTokens(markup);
  const mentions: PickedMention[] = [];
  let text = '';
  let cursor = 0;
  for (const token of tokens) {
    text += markup.slice(cursor, token.start) + `@${token.label}`;
    cursor = token.end;
    const { target } = token;
    mentions.push(
      target.kind === 'user'
        ? { label: token.label, type: 'user', id: target.id }
        : target.kind === 'ai'
          ? { label: token.label, type: AI_BUILDER_MENTION_TYPE, id: AI_BUILDER_MENTION_ID }
          : { label: token.label, type: target.ref.type, id: target.ref.id }
    );
  }
  text += markup.slice(cursor);
  return { text, mentions };
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function mentionPattern(mentions: PickedMention[]): RegExp | null {
  const labels = [...new Set(mentions.map((m) => m.label))].sort((a, b) => b.length - a.length);
  if (!labels.length) return null;
  return new RegExp(`@(${labels.map(escapeRegExp).join('|')})(?![\\w])`, 'g');
}

/** Turn `@Label` back into markup for every picked mention still in the text. */
export function displayToMarkup(text: string, mentions: PickedMention[]): string {
  const byLabel = new Map<string, PickedMention>();
  for (const m of mentions) byLabel.set(m.label, m);
  const pattern = mentionPattern(mentions);
  if (!pattern) return text;
  return text.replace(pattern, (_whole, label: string) => {
    const m = byLabel.get(label)!;
    return mentionMarkup(m.label, m.type, m.id);
  });
}

/** Where the picked mentions sit in the text (for colouring them in the text box). */
export function mentionRanges(text: string, mentions: PickedMention[]): Array<{ start: number; end: number }> {
  const pattern = mentionPattern(mentions);
  if (!pattern) return [];
  return [...text.matchAll(pattern)].map((m) => ({ start: m.index ?? 0, end: (m.index ?? 0) + m[0].length }));
}

/**
 * The `@query` being typed just before the caret, if any. Queries may contain spaces
 * ("@Dutch oven"); the picker hides itself when nothing matches, so a sentence after an @
 * doesn't keep it open. A picked mention whose whole name is still there is finished, but
 * backspacing into it makes what's left a search again.
 */
export function activeMentionQuery(
  text: string,
  caret: number,
  picked: PickedMention[] = []
): { query: string; start: number } | null {
  const before = text.slice(0, caret);
  const match = /(^|\s)@((?:[^\s@][^@\n]{0,39})?)$/.exec(before);
  if (!match) return null;
  const query = match[2];
  const finished = picked.some(
    (m) => query === m.label || (query.startsWith(m.label) && !/\w/.test(query.charAt(m.label.length)))
  );
  if (query && finished) return null;
  return { query, start: before.length - query.length - 1 };
}

/**
 * What saving a description adds to a task, as the server does it: people @'d for the first
 * time are assigned, and things @'d are linked. Used to show both straight away.
 */
export function mentionAdditions(
  before: string,
  after: string,
  task: { assignee_ids: string[]; contexts: TaskContext[] },
  memberIds: string[]
): { assignee_ids?: string[]; contexts?: TaskContext[] } {
  const was = parseMentions(before);
  const now = parseMentions(after);
  const people = now.userIds.filter(
    (id) => !was.userIds.includes(id) && !task.assignee_ids.includes(id) && memberIds.includes(id)
  );
  const linked = new Set(task.contexts.map(taskContextKey));
  const things = now.contexts
    .filter((c) => !linked.has(taskContextKey(c)))
    .map((c): TaskContext => ({ type: c.type, id: c.id, title: c.title, url: '', parent: null, source: 'mention' }));
  return {
    ...(people.length ? { assignee_ids: [...task.assignee_ids, ...people] } : {}),
    ...(things.length ? { contexts: [...task.contexts, ...things] } : {}),
  };
}
